// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

const ROLES = [
  { id: 1, name: "Owner", description: "Full system access with all permissions", color: "#ef4444", users: ["Ryan Miskulin"] },
  { id: 2, name: "Admin", description: "Administrative access to all features", color: "#f97316", users: ["Jordan Lutz"] },
  { id: 3, name: "Manager", description: "Team and project management access", color: "#eab308", users: ["Taylor Schenher", "Tyler Hodgson"] },
  { id: 4, name: "Developer", description: "Development tools and deployment access", color: "#10b981", users: ["Jordan Lutz"] },
  { id: 5, name: "Sales", description: "CRM, pipeline, and client management", color: "#3b82f6", users: ["Lucas Mason", "Tyson Becker", "Des Bachynski"] },
  { id: 6, name: "Accounting", description: "Financial reports and accounting tools", color: "#8b5cf6", users: ["Clorice Robertson", "Taylor Schenher"] },
  { id: 7, name: "Ambassador", description: "External relations and sector management", color: "#ec4899", users: ["Des Bachynski", "Lee Mehling", "Tyson Becker"] },
  { id: 8, name: "Viewer", description: "Read-only access to dashboards and reports", color: "#64748b", users: [] },
];

const FEATURES = [
  "Dashboard", "Tasks", "Sprints", "Team Directory", "CRM Contacts",
  "Deals Pipeline", "Client Meetings", "Follow-Ups", "Activity Feed",
  "Announcements", "Projects", "Reports", "Finance", "Settings",
  "Roles & Permissions", "Overwatch"
];

const PERMISSIONS = {
  Owner:       [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  Admin:       [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  Manager:     [1,1,1,1,1,1,1,1,1,1,1,1,0,1,0,0],
  Developer:   [1,1,1,1,0,0,0,0,1,1,1,0,0,0,0,1],
  Sales:       [1,1,0,1,1,1,1,1,1,1,0,1,0,0,0,0],
  Accounting:  [1,1,0,1,0,0,0,0,1,1,0,1,1,0,0,0],
  Ambassador:  [1,1,0,1,1,1,1,1,1,1,0,1,0,0,0,0],
  Viewer:      [1,0,0,1,0,0,0,0,1,1,0,0,0,0,0,0],
};

export default function RolesPermissionsPage() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", description: "" });

  return (
    <div>
      <div className="page-header">
        <div><h1>Roles & Permissions</h1><p>Manage access control across the platform</p></div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>+ New Role</button>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total Roles</div><div className="stat-value">{ROLES.length}</div></div>
        <div className="stat-card"><div className="stat-label">Features</div><div className="stat-value">{FEATURES.length}</div></div>
        <div className="stat-card"><div className="stat-label">Users Assigned</div><div className="stat-value">{new Set(ROLES.flatMap(r => r.users)).size}</div></div>
        <div className="stat-card"><div className="stat-label">Unassigned Roles</div><div className="stat-value" style={{ color: "#64748b" }}>{ROLES.filter(r => r.users.length === 0).length}</div></div>
      </div>

      {/* Role Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16, marginTop: 16 }}>
        {ROLES.map(role => (
          <div key={role.id} className="card" style={{ cursor: "pointer", borderLeft: `3px solid ${role.color}`, borderColor: selectedRole === role.id ? role.color : undefined }} onClick={() => setSelectedRole(selectedRole === role.id ? null : role.id)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: role.color }}>{role.name}</div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{role.description}</div>
              </div>
              <span className="badge" style={{ background: role.color + "22", color: role.color }}>{role.users.length} users</span>
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Permissions: {PERMISSIONS[role.name]?.filter(p => p === 1).length || 0} / {FEATURES.length}</div>
              <div style={{ height: 4, background: "#334155", borderRadius: 2 }}>
                <div style={{ width: ((PERMISSIONS[role.name]?.filter(p => p === 1).length || 0) / FEATURES.length * 100) + "%", height: 4, background: role.color, borderRadius: 2 }} />
              </div>
            </div>
            {role.users.length > 0 && (
              <div style={{ marginTop: 10, display: "flex", gap: 4, flexWrap: "wrap" }}>
                {role.users.map(u => (
                  <span key={u} style={{ fontSize: 11, padding: "2px 8px", background: "#1e293b", border: "1px solid #334155", borderRadius: 12, color: "#e2e8f0" }}>{u}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Permission Matrix */}
      <div className="card" style={{ marginTop: 24, overflowX: "auto" }}>
        <div className="card-header"><h3>Permission Matrix</h3></div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "2px solid #334155", color: "#94a3b8", position: "sticky", left: 0, background: "#1e293b", minWidth: 160 }}>Feature</th>
              {ROLES.map(r => (
                <th key={r.id} style={{ padding: "10px 8px", borderBottom: "2px solid #334155", color: r.color, textAlign: "center", minWidth: 80, fontSize: 11 }}>{r.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FEATURES.map((feature, fi) => (
              <tr key={feature} style={{ background: fi % 2 === 0 ? "transparent" : "#0f172a11" }}>
                <td style={{ padding: "8px 12px", borderBottom: "1px solid #1e293b", color: "#e2e8f0", position: "sticky", left: 0, background: "#1e293b" }}>{feature}</td>
                {ROLES.map(r => {
                  const hasPermission = PERMISSIONS[r.name]?.[fi] === 1;
                  return (
                    <td key={r.id} style={{ textAlign: "center", padding: "8px", borderBottom: "1px solid #1e293b" }}>
                      {hasPermission ? (
                        <span style={{ color: "#10b981", fontSize: 16, fontWeight: 700 }}>&#10003;</span>
                      ) : (
                        <span style={{ color: "#334155", fontSize: 14 }}>--</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* User-Role Assignments */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><h3>User Assignments</h3></div>
        {[
          { name: "Ryan Miskulin", roles: ["Owner"] },
          { name: "Jordan Lutz", roles: ["Admin", "Developer"] },
          { name: "Taylor Schenher", roles: ["Manager", "Accounting"] },
          { name: "Tyler Hodgson", roles: ["Manager"] },
          { name: "Lucas Mason", roles: ["Sales"] },
          { name: "Des Bachynski", roles: ["Sales", "Ambassador"] },
          { name: "Lee Mehling", roles: ["Ambassador"] },
          { name: "Clorice Robertson", roles: ["Accounting"] },
          { name: "Tyson Becker", roles: ["Sales", "Ambassador"] },
        ].map((user, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #334155" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#10b981", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>
                {user.name.split(" ").map(n => n[0]).join("")}
              </div>
              <span style={{ fontSize: 14, color: "#e2e8f0", fontWeight: 600 }}>{user.name}</span>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {user.roles.map(r => {
                const role = ROLES.find(rl => rl.name === r);
                return <span key={r} className="badge" style={{ background: (role?.color || "#64748b") + "22", color: role?.color || "#64748b" }}>{r}</span>;
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Create Role Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Role</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-field">
                <label>Role Name</label>
                <input className="input" value={newRole.name} onChange={e => setNewRole({ ...newRole, name: e.target.value })} placeholder="Enter role name" />
              </div>
              <div className="form-field">
                <label>Description</label>
                <input className="input" value={newRole.description} onChange={e => setNewRole({ ...newRole, description: e.target.value })} placeholder="Describe this role" />
              </div>
              <div className="form-field">
                <label>Permissions</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {FEATURES.map(f => (
                    <label key={f} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#e2e8f0", cursor: "pointer" }}>
                      <input type="checkbox" style={{ accentColor: "#10b981" }} />
                      {f}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => setShowCreateModal(false)}>Create Role</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
